import { useEffect, useCallback } from "react";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "./index";
import { StoreStatus } from "./StoreStatus";
import { initialStoreStateBase, StoreStateBase } from "./StoreStateBase";

import type { ShoppingCartGet } from "../domain/ShoppingCartSlice/ShoppingCartGet";
import type { ShoppingCartInsert } from "../domain/ShoppingCartSlice/ShoppingCartInsert";
import type { ShoppingCartItemGet } from "../domain/ShoppingCartSlice/ShoppingCartItemGet";
import type { ShoppingCartItemInsert } from "../domain/ShoppingCartSlice/ShoppingCartItemInsert";
import type { ShoppingCartItemClientMeInsert } from "../domain/ShoppingCartSlice/ShoppingCartItemClientMeInsert";
import type { ShoppingCartItemInsertBase } from "../domain/ShoppingCartSlice/ShoppingCartItemInsertBase";
import type { ShoppingCartItemUpdateBase } from "../domain/ShoppingCartSlice/ShoppingCartItemUpdateBase";
import type { ShoppingCartGetAnon } from "../domain/ShoppingCartSlice/ShoppingCartGetAnon";
import type { RouteGetBase } from "../domain/RouteSlice/RouteGetBase";
import type { TotalDetail } from "../domain/PaymentsSlice/TotalDetail";
import type { PromoCodeGet } from "../domain/PromoCodesSlice/PromoCodeGet";

import { ShoppingCartRepository } from "../infrastructure/ShoppingCartSlice/ShoppingCartRepository";
import { ShoppingCartClientMeRepository } from "../infrastructure/ShoppingCartSlice/ShoppingCartClientMeRepository";
import { ShoppingCartItemsClientMeRepository } from "../infrastructure/ShoppingCartSlice/ShoppingCartItemsClientMeRepository";
import { ShoppingCartItemsRepository } from "../infrastructure/ShoppingCartSlice/ShoppingCartItemsRepository";
import Logger from "../infrastructure/Logging/Logger";
import { buildTotals } from "../application/PromoCodesSlice/rules";
import { ensureAnonSessionIdFrom } from "./useAnonStore";

// anon session slice
import {
  setAnonSession,
  clearAnonSession,
  selectAnonSession,
} from "./useShoppingCartAnonSessionStore";

// promos slice (proxy)
import {
  doPromosFetchVisible,
  doPromosValidateAndMaybeApply,
  clearPromo as promosClear,
  selectPromos,
  selectAppliedPromo,
  getOrderPromo,
  getLinePromos,
  getPromoForProductBadge,
  getPromoForProductPricing,
  type ApplyPromoResult,
  selectPromosStatus,
  getOrderPromoReadOnly,
} from "./usePromosStore";
import { selectIsLoggedInClient } from "./useSecurityStore";
import globalRoles from "../globalRoles";

// ===== State
export interface ShoppingCartState extends StoreStateBase {
  cart: ShoppingCartGet | null;
  hasSubmittedOrder: boolean;
  noCartProbed: boolean;
  selectedRoute?: RouteGetBase;
}

const initialState: ShoppingCartState = {
  ...initialStoreStateBase,
  cart: null,
  hasSubmittedOrder: false,
  noCartProbed: false,
  selectedRoute: undefined,
};

// ===== selectors
export const selectCart = (s: RootState) => s.shoppingCart.cart;
export const selectCartStatus = (s: RootState) => s.shoppingCart.storeStatus;
export const selectHasSubmittedOrder = (s: RootState) =>
  s.shoppingCart.hasSubmittedOrder;
export const selectNoCartProbed = (s: RootState) => s.shoppingCart.noCartProbed;
export const selectSelectedRoute = (s: RootState) => s.shoppingCart.selectedRoute;

// ---- Centralized stock math (pure selectors) ----
function computeAvailableOnTheFlyNormalized(
  s: RootState,
  itemId: number
): {
  qtyInCart: number;
  available: number;
  isOutOfInventory: boolean;
} {
  const cart = s.shoppingCart.cart;
  const item = cart?.shoppingCartItems?.find(
    (i) => i.shoppingCartItemId === itemId
  );
  if (!cart || !item || !item.product)
    return { qtyInCart: 0, available: 0, isOutOfInventory: false };

  const product = item.product;

  // Snapshot reducer for “components already used in other lines”
  const stockUsedInCompositions = (cart.shoppingCartItems ?? []).reduce(
    (acc, line) => {
      const comps = line.product?.productCompositions ?? [];
      comps.forEach((comp) => {
        if (comp.componentProductId === product.productId) {
          acc = line.quantity * comp.quantityNormalizedForStock;
        }
      });
      return acc;
    },
    0
  );

  const productStockReductionRatio =
    (product.productCompositions?.length ?? 0) > 0
      ? 1
      : product.availablePurchasesNormalized / product.stockQuantity;

  const minStockQuantityOfCompositionsStockOnCart =
    (product.productCompositions?.length ?? 0) > 0
      ? Math.min(
          ...product.productCompositions!.map((comp) => {
            const compStock = comp.componentProduct.stockQuantity;
            const perUnit = comp.quantityNormalizedForStock || 0;
            if (perUnit <= 0) return Number.POSITIVE_INFINITY;
            const cartLineForComp = cart.shoppingCartItems?.find(
              (l) => l.productId === comp.componentProductId
            );
            const qtyInCartForComp = cartLineForComp?.quantity || 0;
            const availableComp =
              compStock - qtyInCartForComp * perUnit - stockUsedInCompositions;
            return Math.floor(availableComp / perUnit);
          })
        )
      : 0;

  const qtyInCart = item.quantity || 0;
  const baseAvail =
    (product.productCompositions?.length ?? 0) > 0
      ? minStockQuantityOfCompositionsStockOnCart
      : product.availablePurchasesNormalized;

  const available = Math.floor(
    baseAvail - qtyInCart - stockUsedInCompositions * productStockReductionRatio
  );

  const isOutOfInventory = product.availablePurchasesNormalized < 1;
  return { qtyInCart, available, isOutOfInventory };
}

/** Selector: single source of truth for item conflict */
export function hasItemBlockingConflictSelector(
  s: RootState,
  itemId: number
): boolean {
  const { qtyInCart, available, isOutOfInventory } =
    computeAvailableOnTheFlyNormalized(s, itemId);
  return stockPolicy.hasBlockingConflict(
    qtyInCart,
    available,
    isOutOfInventory
  );
}

/** Selector: “taking the last unit” (allowed) */
export function isTakingLastUnitSelector(
  s: RootState,
  itemId: number
): boolean {
  const { qtyInCart, available, isOutOfInventory } =
    computeAvailableOnTheFlyNormalized(s, itemId);
  return stockPolicy.isLastUnit(qtyInCart, available, isOutOfInventory);
}

export const selectHasStockConflicts = (s: RootState) => {
  const cart = s.shoppingCart.cart;
  if (!cart?.shoppingCartItems?.length) return false;

  // Treat products that became snapshots (no longer available in catalog)
  // as a blocking conflict to disable checkout until removed.
  const hasNoLongerAvailable = cart.shoppingCartItems.some(
    (it) => !!it.product?.productBecameSnapshotDate
  );
  if (hasNoLongerAvailable) return true;

  return cart.shoppingCartItems.some((it) =>
    hasItemBlockingConflictSelector(s, it.shoppingCartItemId)
  );
};

// === Central stock policy (single source of truth) ===
// Inputs are the numbers each card (or any caller) already computes.
export const stockPolicy = {
  // Last unit = allowed to proceed:
  // - user has some qty in cart
  // - on-the-fly availability is exactly 0
  // - AND product is NOT globally out of inventory
  isLastUnit(
    qtyInCart: number,
    availableOnTheFly: number,
    isOutOfInventory: boolean
  ) {
    return qtyInCart > 0 && availableOnTheFly === 0 && !isOutOfInventory;
  },
  // Blocking conflict = must adjust before checkout:
  // - availability went BELOW 0 (classic overdraw), OR
  // - product became globally out of inventory while user has items in cart
  hasBlockingConflict(
    qtyInCart: number,
    availableOnTheFly: number,
    isOutOfInventory: boolean
  ) {
    return qtyInCart > 0 && (availableOnTheFly < 0 || isOutOfInventory);
  },
};

// ===== thunks
// ⬇️ at the top of doShoppingCartFetch
export const doShoppingCartFetch = createAsyncThunk<
  ShoppingCartGet | null,
  void,
  { state: RootState }
>("shoppingCart/fetch", async (_arg, { getState }) => {
  try {
    const s = getState();
    const isLoggedInClient = s.security.isLoggedIn && s.security.roles.includes(globalRoles.CLIENT);

    if (isLoggedInClient) {
      const fetched =
        await new ShoppingCartClientMeRepository().getOneAuthenticated();
      return fetched ?? null;
    }

    // anon mode:
    const anon = selectAnonSession(s);
    // ⬇️ Harden the guard
    if (!anon || !anon.sessionId || !anon.shoppingCartId) {
      return null; // absolutely no API calls
    }

    const fetched = await new ShoppingCartRepository().fetchShoppingCart(anon);
    return fetched ?? null;
  } catch (e) {
    Logger.error(`Error fetching shopping cart: ${e}`);
    return null;
  }
});

export const doShoppingCartAddItem = createAsyncThunk<
  ShoppingCartGet | ShoppingCartGetAnon | null,
  { item: ShoppingCartItemInsertBase },
  { state: RootState }
>("shoppingCart/addItem", async ({ item }, { getState, dispatch }) => {
  const { productId, quantity, price, discount, promoCodeId } = item;
  const s = getState();
  const isLoggedInClient = s.security.isLoggedIn && s.security.roles.includes(globalRoles.CLIENT);
  const cart = s.shoppingCart.cart;

  if (!cart) {
    const newCart = { shoppingCartItems: [ { productId, quantity, price, discount, promoCodeId } ] } as ShoppingCartInsert;

    if (isLoggedInClient) {
      const created =
        await new ShoppingCartClientMeRepository().insertAuthenticated<ShoppingCartGet>(newCart);
      return created ?? null;
    } else {
      const repo = new ShoppingCartRepository();
      newCart.sessionId = ensureAnonSessionIdFrom(s, dispatch);
      const created = await repo.insert<ShoppingCartGetAnon>(newCart);
      if (created) {
        dispatch(
          setAnonSession({
            sessionId: created.sessionId,
            shoppingCartId: created.shoppingCartId,
          })
        );
      }
      return created ?? null;
    }
  } else {
    let newCartItem: ShoppingCartItemGet | null = null;
    if (isLoggedInClient) {
      newCartItem =
        await new ShoppingCartItemsClientMeRepository().insertAuthenticated<ShoppingCartItemGet>(
          {
            productId,
            quantity,
            price,
            discount,
            promoCodeId,
            shoppingCartId: cart.shoppingCartId,
          } as ShoppingCartItemClientMeInsert
        );
    } else {
      const anon = selectAnonSession(s);
      if (anon) {
        newCartItem =
          await new ShoppingCartItemsRepository().insert<ShoppingCartItemGet>({
            productId,
            quantity,
            price,
            discount,
            promoCodeId,
            ...anon,
          } as ShoppingCartItemInsert);
      }
    }
    if (!newCartItem) return cart; // no change
    return {
      ...cart,
      shoppingCartItems: [...(cart.shoppingCartItems ?? []), newCartItem],
    } as ShoppingCartGet;
  }
});

export const doShoppingCartUpdateItem = createAsyncThunk<
  ShoppingCartGet | null,
  { update: ShoppingCartItemUpdateBase },
  { state: RootState }
>("shoppingCart/updateItem", async ({ update }, { getState }) => {
  const s = getState();
  const isLoggedInClient = s.security.isLoggedIn && s.security.roles.includes(globalRoles.CLIENT);
  const cart = s.shoppingCart.cart;
  if (!cart) return null;

  if (isLoggedInClient) {
    await new ShoppingCartItemsClientMeRepository().updateAuthenticated(update);
  } else {
    const anon = selectAnonSession(s);
    if (!anon) return cart;
    await new ShoppingCartItemsRepository().update({
      ...update,
      sessionId: anon.sessionId,
    });
  }

  return {
    ...cart,
    shoppingCartItems: (cart.shoppingCartItems ?? []).map((it) =>
      it.shoppingCartItemId === update.shoppingCartItemId
        ? { ...it, ...update }
        : it
    ),
  } as ShoppingCartGet;
});

export const doShoppingCartDeleteItem = createAsyncThunk<
  ShoppingCartGet | null,
  { shoppingCartItemId: number },
  { state: RootState }
>(
  "shoppingCart/deleteItem",
  async ({ shoppingCartItemId }, { getState, dispatch }) => {
    const s = getState();
    const isLoggedInClient = s.security.isLoggedIn && s.security.roles.includes(globalRoles.CLIENT);
    const cart = s.shoppingCart.cart;
    if (!cart || !cart.shoppingCartItems) return null;

    const remaining = cart.shoppingCartItems.filter(
      (i) => i.shoppingCartItemId !== shoppingCartItemId
    );

    if (remaining.length === 0) {
      if (isLoggedInClient) {
        // Logged client: delete full cart on server
        await new ShoppingCartClientMeRepository().deleteAuthenticated({});
      } else {
        // Anon: delete full cart on server and clear persisted anon session
        const anon = selectAnonSession(s);
        if (anon) {
          await new ShoppingCartRepository().delete(anon);
          dispatch(clearAnonSession());
        }
      }
      // Tell reducers there is no cart anymore
      return null;
    } else {
      // Partial delete → remove item only
      if (isLoggedInClient) {
        await new ShoppingCartItemsClientMeRepository().deleteAuthenticated({
          shoppingCartItemId,
        });
      } else {
        const anon = selectAnonSession(s);
        if (anon) {
          await new ShoppingCartItemsRepository().delete({
            shoppingCartItemId,
            ...anon,
          });
        }
      }
      // Local optimistic patch
      return { ...cart, shoppingCartItems: remaining } as ShoppingCartGet;
    }
  }
);

// ===== slice
export const shoppingCartSlice = createSlice({
  name: "shoppingCart",
  initialState,
  reducers: {
    reset: () => initialState,
    setHasSubmittedOrder(s, a: PayloadAction<boolean>) {
      s.hasSubmittedOrder = a.payload;
    },
    setSelectedRoute(s, a: PayloadAction<RouteGetBase | undefined>) {
      s.selectedRoute = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(doShoppingCartFetch.pending, (s) => {
      s.storeStatus = StoreStatus.loading;
    })
      .addCase(doShoppingCartFetch.fulfilled, (state, action) => {
        state.storeStatus = StoreStatus.succeeded;
        state.cart = action.payload ?? null; // <- important: allow null
        state.error = null;
        state.noCartProbed = action.payload == null;
      })
      .addCase(doShoppingCartFetch.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Fetch cart failed");
      })
      .addCase(doShoppingCartAddItem.pending, (s) => {
        s.storeStatus = StoreStatus.loading;
      })
      .addCase(doShoppingCartAddItem.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.cart = (a.payload as ShoppingCartGet | null) ?? s.cart;
        if (a.payload) s.noCartProbed = false;
      })
      .addCase(doShoppingCartAddItem.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Add item failed");
      })
      .addCase(doShoppingCartUpdateItem.pending, (s) => {
        s.storeStatus = StoreStatus.loading;
      })
      .addCase(doShoppingCartUpdateItem.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.cart = a.payload ?? s.cart;
        if (a.payload) s.noCartProbed = false;
      })
      .addCase(doShoppingCartUpdateItem.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Update item failed");
      })
      .addCase(doShoppingCartDeleteItem.fulfilled, (state, action) => {
        state.storeStatus = StoreStatus.succeeded;
        state.cart = action.payload ?? null; // <- allow null when last item removed
        state.error = null;
        state.noCartProbed = action.payload == null;
      })
      .addCase(doShoppingCartDeleteItem.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Delete item failed");
      });
  },
});

export default shoppingCartSlice.reducer;
export const { setHasSubmittedOrder } = shoppingCartSlice.actions;

export function useShoppingCartBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const cart = useSelector(selectCart);
  const noCartProbed = useSelector(selectNoCartProbed);
  const isLoggedInClient = useSelector(selectIsLoggedInClient);
  const anonSession = useSelector(selectAnonSession);

  // Fetch on mount and whenever auth state flips (login/logout/rehydrate)
  useEffect(() => {
    // absolutely skip when there is no session of any kind
    if (!isLoggedInClient && !anonSession) {
      return;
    }

    // if we already probed and confirmed there's no cart, do not loop
    if (!cart && noCartProbed) {
      return;
    }

    // only fetch when we actually need data
    if (!cart) {
      dispatch(doShoppingCartFetch());
      dispatch(doPromosFetchVisible());
    }
  }, [
    dispatch,
    isLoggedInClient,
    anonSession?.sessionId,
    cart?.shoppingCartId,
    noCartProbed,
  ]);
}

// ===== facade hook (single hook for cart + promos)
export function useShoppingCartStore() {
  const dispatch = useDispatch<AppDispatch>();

  // cart state
  const cart = useSelector(selectCart);
  const status = useSelector(selectCartStatus);
  const hasSubmittedOrder = useSelector(selectHasSubmittedOrder);
  const hasStockConflicts = useSelector(selectHasStockConflicts);

  // promos state (proxied)
  const promos = useSelector(selectPromos);
  const appliedPromo = useSelector(selectAppliedPromo);
  const promosStatus = useSelector(selectPromosStatus);
  const selectedRoute = useSelector(selectSelectedRoute);

  // helpers (same logic)
  const getSubtotal = useCallback(
    () =>
      cart?.shoppingCartItems?.reduce(
        (s, it) => s + it.price * it.quantity,
        0
      ) ?? 0,
    [cart]
  );

  const getTotalDetail = useCallback(
    (creditBalance: number = 0): TotalDetail => {
      const subtotal = getSubtotal();
      const orderPromo = getOrderPromo(promos, appliedPromo, subtotal);
      const linePromos: PromoCodeGet[] = orderPromo
        ? []
        : getLinePromos(promos, appliedPromo);
      return buildTotals(
        cart,
        selectedRoute,
        orderPromo,
        linePromos,
        creditBalance
      );
    },
    [cart, promos, appliedPromo, getSubtotal, selectedRoute]
  );

  // cart API
  const addToCart = useCallback(
    (item: ShoppingCartItemInsertBase) =>
      dispatch(doShoppingCartAddItem({ item })),
    [dispatch]
  );

  const updateCartItem = useCallback(
    (update: ShoppingCartItemUpdateBase) =>
      dispatch(doShoppingCartUpdateItem({ update })),
    [dispatch]
  );

  const deleteCartItem = useCallback(
    (shoppingCartItemId: number) =>
      dispatch(doShoppingCartDeleteItem({ shoppingCartItemId })),
    [dispatch]
  );

  const resetShoppingCart = useCallback(
    () => dispatch(doShoppingCartFetch()),
    [dispatch]
  );

  const setHasSubmittedOrderAction = useCallback(
    (v: boolean) => dispatch(setHasSubmittedOrder(v)),
    [dispatch]
  );

  const setSelectedRouteAction = useCallback(
    (r?: RouteGetBase) => dispatch(shoppingCartSlice.actions.setSelectedRoute(r)),
    [dispatch]
  );

  // promos API (proxied)
  const applyPromo = useCallback(
    async (code: string): Promise<ApplyPromoResult> => {
      const result = await dispatch(
        doPromosValidateAndMaybeApply({ code, subtotal: getSubtotal() })
      ).unwrap();
      return result;
    },
    [dispatch, getSubtotal]
  );

  const resetPromos = useCallback(async () => {
    // clear and re-fetch visible (identical behavior to original hook)
    dispatch(promosClear());
    await dispatch(doPromosFetchVisible());
  }, [dispatch]);

  const clearPromo = useCallback(() => {
    dispatch(promosClear());
  }, [dispatch]);

  const getBestVisibleOrderPromo = useCallback(
    () => getOrderPromo(promos, null, getSubtotal()),
    [promos, getSubtotal]
  );

  const getBestVisibleOrderPromoReadOnly = useCallback(
    () => getOrderPromoReadOnly(promos, getSubtotal()),
    [promos, getSubtotal]
  );

  const getPromoBadgeForProduct = useCallback(
    (productId: number) =>
      getPromoForProductBadge(promos, appliedPromo, productId),
    [promos, appliedPromo]
  );

  const computeLineDiscount = useCallback(
    (productId: number, unitPrice: number) =>
      getPromoForProductPricing(
        promos,
        appliedPromo,
        productId,
        unitPrice,
        getSubtotal()
      ),
    [promos, appliedPromo, getSubtotal]
  );

  return {
    // cart state
    cart,
    status,
    isLoadingShoppingCart: status === StoreStatus.loading,
    hasSubmittedOrder,
    setHasSubmittedOrder: setHasSubmittedOrderAction,
    hasItemBlockingConflict: (itemId: number) =>
      hasItemBlockingConflictSelector(
        (dispatch as any).getState?.() ?? ({} as RootState),
        itemId
      ),
    isTakingLastUnitById: (itemId: number) =>
      isTakingLastUnitSelector(
        (dispatch as any).getState?.() ?? ({} as RootState),
        itemId
      ),
    hasStockConflicts,
    // expose stock policy helpers so components/hooks don't reimplement rules
    stockPolicy,

    // cart ops
    addToCart,
    updateCartItem,
    deleteCartItem,
    getTotalDetail,
    resetShoppingCart,

    // promos (proxied so pages/components only use this hook)
    promos,
    promosStatus,
    resetPromos,
    applyPromo,
    clearPromo,
    getBestVisibleOrderPromo,
    getBestVisibleOrderPromoReadOnly,
    appliedPromo,
    getPromoBadgeForProduct,
    computeLineDiscount,
  // shipping route selection
  selectedRoute,
  setSelectedRoute: setSelectedRouteAction,
  } as const;
}

// re-export reducer for index.ts
export { default as shoppingCart } from "./useShoppingCartStore";
